import { Button, Flex, Modal, SearchField, Skeleton } from '@iheartjane/reefer';
import { useFormContext } from '@iheartjane/reefer-hook-form';
import get from 'lodash-es/get';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useParams } from 'react-router';

import {
  useFetchBrands,
  useFetchGlobalBrands,
  useFetchTargetedBrands,
} from '@jane/business-admin/data-access';
import { useRetailOrgSettings } from '@jane/business-admin/hooks';
import type { Brand } from '@jane/business-admin/types';
import { ModalNames, blankArrayOfLength } from '@jane/business-admin/util';

import { ConfirmWrapperWithTracking } from '../../../../../ConfirmWrapperWithTracking';
import { ConditionType } from '../../form';
import {
  Condition,
  ConditionByModal,
} from '../generic_components/ConditionByModal';
import { LoadingCondition } from '../generic_components/LoadingCondition';
import { BrandCheckboxRow } from './BrandCheckboxRow';

const BrandPickerLoading = () => {
  const length10Array = blankArrayOfLength(10);

  return (
    <>
      {length10Array.map((i) => (
        <Skeleton
          animate
          height={72}
          style={{
            boxShadow: 'inset 0px -1px 0px rgba(0, 0, 0, 0.1)',
          }}
          key={i}
        >
          <Flex py={12} px={24} height={72} alignItems="center">
            <Skeleton.Bone borderRadius="circular" width={46} height={46} />
            <Skeleton.Bone width="90%" ml={24} />
          </Flex>
        </Skeleton>
      ))}
    </>
  );
};

interface BrandObj {
  name: string;
  productBrandId: number;
}

export const BrandConditions = ({
  disableAddButton,
  fieldPrefix,
  isGlobalSpecial,
  type,
}: {
  disableAddButton?: boolean;
  fieldPrefix: string;
  isGlobalSpecial?: boolean;
  type: ConditionType.Brands | ConditionType.ExcludeBrands;
}) => {
  const { id: storeId = '' } = useParams<'id'>();
  const { query_enabled, org_id } = useRetailOrgSettings();

  const [query, setQuery] = useState('');
  const [brandConditionsModalOpen, setBrandConditionsModalOpen] =
    useState(false);

  const { setValue, watch } = useFormContext();

  const rulesWatch = watch(`${fieldPrefix}.rules`);
  const includesOrExcludes =
    type === ConditionType.Brands ? 'includes' : 'excludes';

  // brands previously targeted by the special. used to set initial state for editing
  const initialSelectedBrandIds: number[] = get(
    rulesWatch,
    `${includesOrExcludes}.0.brand_ids`,
    []
  );

  const [selectedBrands, setSelectedBrands] = useState<BrandObj[]>(
    initialSelectedBrandIds.map((id) => ({
      productBrandId: id,
      name: '',
    }))
  );
  let initialSelectedBrands: BrandObj[]; // used for reverting the state when discarding changes

  const wrapperRef = useRef(null);

  const useGetBrandData = () => {
    const {
      data: allBrands,
      isFetching: allBrandsFetching,
      isFetchingNextPage: allBrandsFetchingNextPage,
      fetchNextPage: allBrandsFetchNextPage,
    } = useFetchBrands(
      storeId,
      { names: query, order: 'asc' },
      !isGlobalSpecial
    );

    const {
      data: globalBrands,
      isFetching: globalBrandsFetching,
      isFetchingNextPage: globalBrandsFetchingNextPage,
      fetchNextPage: globalBrandsFetchNextPage,
    } = useFetchGlobalBrands(
      { names: query, order: 'asc', retail_org_id: org_id },
      (isGlobalSpecial && query_enabled) || false
    );

    return {
      data: !isGlobalSpecial ? allBrands : globalBrands,
      isFetching: !isGlobalSpecial ? allBrandsFetching : globalBrandsFetching,
      isFetchingNextPage: !isGlobalSpecial
        ? allBrandsFetchingNextPage
        : globalBrandsFetchingNextPage,
      fetchNextPage: !isGlobalSpecial
        ? allBrandsFetchNextPage
        : globalBrandsFetchNextPage,
    };
  };

  // brand data to populate the brand selection modal
  const {
    data: brandData,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetBrandData();

  // Brand data for targeted brands (brands in the conditions/rules).
  // We fetch this separately to ensure we always have names/ids for selected brands
  // when an existing special targets a brand not returned in the first page of brandData
  const { data: targetedBrandData, isSuccess: targetedBrandSuccess } =
    useFetchTargetedBrands(
      initialSelectedBrandIds.join(','),
      fieldPrefix,
      includesOrExcludes
    );

  // add names to selectedBrands, set initialSelectedBrands
  /** @todo adjust for hook immutability */
  // eslint-disable-next-line react-hooks/immutability
  useEffect(() => {
    if (targetedBrandSuccess) {
      const newSelectedBrands = selectedBrands.map((brandObj) => {
        const brand = targetedBrandData?.find(
          (brand) => brand.productBrandId === brandObj.productBrandId
        );

        return {
          productBrandId: brand?.productBrandId,
          name: brand?.name,
        } as BrandObj;
      });

      setSelectedBrands(newSelectedBrands);
      // eslint-disable-next-line react-hooks/immutability
      initialSelectedBrands = newSelectedBrands;
    }
  }, [JSON.stringify(targetedBrandData)]);

  const { ref } = useInView({
    root: wrapperRef.current,
    threshold: 0,
    onChange: (inView) => {
      if (inView) {
        fetchNextPage();
      }
    },
    skip: isFetchingNextPage,
  });

  const rules =
    rulesWatch && rulesWatch[includesOrExcludes]
      ? rulesWatch[includesOrExcludes]
      : null;

  // when adding new brand, data is selectedBrands
  // when removing by clicking pill, data is the remaining Conditions
  // when removing all, data is []
  // selectedBrands will only change when removing one or all brands
  const onSubmit = (data: Condition[] | BrandObj[]) => {
    const isConditionArray = (
      value: Condition[] | BrandObj[]
    ): value is Condition[] => Condition.array().safeParse(value).success;

    // ensure all selected brands are represented with names & IDs in the conditions/rules we send to the server
    const idValues = isConditionArray(data)
      ? data.map(({ value }) => parseInt(value))
      : data.map(({ productBrandId }) => productBrandId);

    const newSelectedBrands = selectedBrands.filter((obj) =>
      idValues.includes(obj.productBrandId)
    );

    const nameValues = newSelectedBrands.map((obj) => obj.name);

    const firstCondition = rules?.[0] ?? {};
    setValue(
      `${fieldPrefix}.rules`,
      {
        ...rulesWatch,
        [includesOrExcludes]: [
          { ...firstCondition, brands: nameValues, brand_ids: idValues },
        ],
      },
      { shouldDirty: true }
    );

    setSelectedBrands(newSelectedBrands);
    setBrandConditionsModalOpen(false);
    setQuery('');
  };

  const select = (brand: Brand) => {
    // We are de-selecting a brand, so we filter it out
    const newSelectedBrands = selectedBrands.filter(
      (brandObj) => brandObj.productBrandId !== brand.productBrandId
    );

    // We are selecting a brand since our newly selected brand was not found in the filter operation and our lengths remain the same
    if (selectedBrands.length === newSelectedBrands.length) {
      newSelectedBrands.push({
        productBrandId: brand.productBrandId,
        name: brand.name,
      });
    }

    setSelectedBrands(newSelectedBrands);
  };

  const isSelected = (brand: Brand) =>
    !!selectedBrands.find((obj) => obj.productBrandId === brand.productBrandId);

  const conditions = selectedBrands.map((brandObj) => ({
    value: brandObj.productBrandId.toString(),
    label: brandObj.name,
  }));

  return (
    <>
      {!(targetedBrandData && brandData) ? (
        <LoadingCondition />
      ) : (
        <ConditionByModal
          disableAddButton={disableAddButton}
          onOpenModal={() => setBrandConditionsModalOpen(true)}
          type={type}
          conditions={conditions}
          conditionsLoading={isFetching}
          onConditionsUpdate={onSubmit}
        />
      )}
      <ConfirmWrapperWithTracking
        open={brandConditionsModalOpen}
        onCloseDiscardChanges={() => {
          setSelectedBrands(initialSelectedBrands);
        }}
        setOpen={() => {
          setQuery('');
          setBrandConditionsModalOpen(false);
        }}
        hasChanges={selectedBrands.length !== conditions?.length}
        modalName={ModalNames.BrandConditions}
        overlayClose={true}
      >
        <>
          <Modal.Header
            title="Select brands"
            actions={
              <Button
                variant="primary"
                label="Save"
                onClick={() => onSubmit(selectedBrands)}
                disabled={selectedBrands.length === 0}
              />
            }
          />
          <Modal.Content ref={wrapperRef}>
            <SearchField
              borderRadius="sm"
              label="Search Brands"
              name="brand-search-field"
              onChange={setQuery}
              placeholder="Search for a brand"
              mb={24}
            />
            {isFetching ? (
              <BrandPickerLoading />
            ) : (
              <>
                {brandData?.map((brand, i) => {
                  const isFirst = i === 0;
                  const isLast = i === brandData.length - 1;
                  const needsDivider = !isFirst;

                  return (
                    <BrandCheckboxRow
                      key={brand.id}
                      brand={brand}
                      onClick={() => select(brand)}
                      selected={isSelected(brand)}
                      divider={needsDivider}
                      ref={isLast ? ref : undefined}
                    />
                  );
                })}
              </>
            )}
          </Modal.Content>
        </>
      </ConfirmWrapperWithTracking>
    </>
  );
};
